import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { SystemEntity, SYSTEM_ENTITY_LABELS } from '@erp/shared-types';

interface EntityLinkRenderProps {
  entityId: string;
  className: string;
  children: ReactNode;
}

/**
 * The detail-page link for each entity type that has one. Because TanStack
 * links are typed per route, each entry renders its own `<Link>` rather than
 * being data-driven; add an entry as more entity types get routable detail
 * pages. This map is the single source for both `SystemEntityLink` and
 * `hasSystemEntityLink`, so the two cannot drift.
 *
 * `AR_COLLECTION_THREAD` is deliberately absent: its detail route is nested
 * under the customer and cannot be built from the thread id alone.
 */
const ENTITY_LINKS: Partial<
  Record<SystemEntity, (props: EntityLinkRenderProps) => ReactNode>
> = {
  [SystemEntity.AR_DISCOUNT_GROUP]: ({ entityId, className, children }) => (
    <Link
      to="/discounts/$groupId"
      params={{ groupId: entityId }}
      viewTransition
      className={className}
    >
      {children}
    </Link>
  ),
  [SystemEntity.AR_REMITTANCE_ADVICE]: ({ entityId, className, children }) => (
    <Link
      to="/accounts-receivable/remittances/$remittanceId"
      params={{ remittanceId: entityId }}
      viewTransition
      className={className}
    >
      {children}
    </Link>
  ),
  [SystemEntity.AR_CASH_RECEIPT]: ({ entityId, className, children }) => (
    <Link
      to="/accounts-receivable/cash-receipts"
      search={{ receiptId: entityId }}
      viewTransition
      className={className}
    >
      {children}
    </Link>
  ),
  [SystemEntity.AP_INVOICE]: ({ entityId, className, children }) => (
    <Link
      to="/accounts-payable/invoices/$invoiceId"
      params={{ invoiceId: entityId }}
      viewTransition
      className={className}
    >
      {children}
    </Link>
  ),
  [SystemEntity.AR_INVOICE]: ({ entityId, className, children }) => (
    <Link
      to="/accounts-receivable/invoices/$arInvoiceId"
      params={{ arInvoiceId: entityId }}
      viewTransition
      className={className}
    >
      {children}
    </Link>
  ),
  [SystemEntity.AR_SALES_ORDER]: ({ entityId, className, children }) => (
    <Link
      to="/accounts-receivable/ingested-sales-orders/$salesOrderId"
      params={{ salesOrderId: entityId }}
      viewTransition
      className={className}
    >
      {children}
    </Link>
  ),
  [SystemEntity.AR_DRAFT_SALES_ORDER]: ({ entityId, className, children }) => (
    <Link
      to="/sales-orders-v3/$draftId"
      params={{ draftId: entityId }}
      viewTransition
      className={className}
    >
      {children}
    </Link>
  ),
  [SystemEntity.WORKBOOK]: ({ entityId, className, children }) => (
    <Link
      to="/workbooks/$workbookId"
      params={{ workbookId: entityId }}
      viewTransition
      className={className}
    >
      {children}
    </Link>
  ),
  [SystemEntity.TASK]: ({ entityId, className, children }) => (
    <Link
      to="/tasks/$taskId"
      params={{ taskId: entityId }}
      viewTransition
      className={className}
    >
      {children}
    </Link>
  ),
};

/** Whether `SystemEntityLink` renders a link (not plain text) for this type. */
export function hasSystemEntityLink(entityType: SystemEntity): boolean {
  return ENTITY_LINKS[entityType] !== undefined;
}

/**
 * Client-side link to an entity's detail page, keyed by `SystemEntity` type.
 *
 * Renders a TanStack `<Link>` for entity types that have a routable detail page
 * and falls back to plain text (the entity's label) for those that don't — so
 * callers can render it unconditionally. Use `hasSystemEntityLink` to choose a
 * different fallback.
 */
export function SystemEntityLink({
  entityType,
  entityId,
  className,
  children,
}: {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
  children?: ReactNode;
}) {
  const label = children ?? SYSTEM_ENTITY_LABELS[entityType];
  const renderLink = ENTITY_LINKS[entityType];
  if (!renderLink) return <span className={className}>{label}</span>;
  return renderLink({
    entityId,
    className: className ?? 'font-medium text-blue-600 hover:underline',
    children: label,
  });
}
